import { Timer } from "lucide-react";
import { useRef } from "react";
import {
	EntryForm,
	type EntryFormHandle,
} from "@/v2/components/entry-form/EntryForm";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { createTimerEntryDefaults } from "@/v2/lib/entryPayloads";
import { formatDurationClock } from "@/v2/lib/time";

export interface AddEntryDialogProps {
	open: boolean;
	date: Date;
	timerPrefillDuration: number | null;
	onOpenChange: (open: boolean) => void;
	onSubmit: (values: TimeEntryFormValues) => void;
	onExport: (values: TimeEntryFormValues) => void;
	title?: string;
}

export function AddEntryDialog({
	open,
	date,
	timerPrefillDuration,
	onOpenChange,
	onSubmit,
	onExport,
	title = "Add Entry",
}: AddEntryDialogProps) {
	const formKey = `${date.toISOString()}-${timerPrefillDuration ?? "manual"}`;
	const formRef = useRef<EntryFormHandle>(null);

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				className="!max-h-[90vh]"
				title={title}
				headerActions={
					timerPrefillDuration !== null ? (
						<TimerElapsedBadge durationSeconds={timerPrefillDuration} />
					) : undefined
				}
				headerClassName="p-4 pb-2 pr-12"
				footer={
					<div className="flex justify-end gap-2">
						<TooltipButton
							type="button"
							variant="outline"
							size="sm"
							tooltip="Export"
							onClick={() => formRef.current?.submitExport()}
						>
							Export
						</TooltipButton>
						<TooltipButton
							type="submit"
							form="add-entry-form"
							size="sm"
							tooltip="Add"
						>
							Add
						</TooltipButton>
					</div>
				}
			>
				<EntryForm
					ref={formRef}
					key={formKey}
					formId="add-entry-form"
					defaultValues={createTimerEntryDefaults({
						date,
						timerDurationSeconds: timerPrefillDuration,
						carryForward: null,
					})}
					onSubmit={onSubmit}
					onExport={onExport}
				/>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

interface TimerElapsedBadgeProps {
	durationSeconds: number;
}

function TimerElapsedBadge({ durationSeconds }: TimerElapsedBadgeProps) {
	return (
		<div className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium">
			<Timer className="size-3.5 text-muted-foreground" />
			<span className="tabular-nums">
				{formatDurationClock(durationSeconds)}
			</span>
		</div>
	);
}
